import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useApp } from "../context/AppContext";
import { t } from "../lib/i18n";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { WhatsAppFab } from "../components/WhatsAppFab";
import { DASH } from "../constants/testIds";
import { CalendarDays, Download, IndianRupee, Package, Bell, XCircle } from "lucide-react";
import { toast } from "sonner";

export default function Dashboard() {
  const { user, lang, loading } = useApp();
  const navigate = useNavigate();
  const [tab, setTab] = useState("bookings");
  const [bookings, setBookings] = useState([]);
  const [orders, setOrders] = useState([]);
  const [notifs, setNotifs] = useState([]);

  useEffect(() => {
    if (!loading && !user) navigate("/login");
  }, [loading, user, navigate]);

  useEffect(() => {
    if (!user) return;
    (async () => {
      const [b, o, n] = await Promise.all([
        api.get("/bookings/me"),
        api.get("/pharmacy/orders/me"),
        api.get("/notifications/me"),
      ]);
      setBookings(b.data); setOrders(o.data); setNotifs(n.data);
    })();
  }, [user]);

  const cancelBooking = async (b) => {
    if (!confirm(lang==="hi"?"क्या आप बुकिंग रद्द करना चाहते हैं?":"Cancel this booking?")) return;
    try {
      await api.post(`/bookings/${b.booking_id}/cancel`);
      setBookings(bookings.map(x => x.booking_id === b.booking_id ? { ...x, status: "cancelled" } : x));
      toast.success(lang==="hi"?"बुकिंग रद्द की गई":"Booking cancelled");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cancel failed");
    }
  };

  const printReceipt = (b) => {
    const w = window.open("", "_blank", "width=680,height=800");
    w.document.write(`
      <html><head><title>Receipt ${b.booking_id}</title>
      <style>body{font-family:sans-serif;padding:24px;color:#0F172A}
      h1{color:#2563EB;margin:0}.row{display:flex;justify-content:space-between;padding:6px 0;border-bottom:1px solid #eee}
      .total{font-weight:700;font-size:18px;margin-top:10px}</style></head><body>
      <h1>AASAN OPD — Receipt</h1>
      <p>Booking ID: ${b.booking_id} · Token: ${b.token_number}</p>
      <div class="row"><span>Service</span><b>${b.service_type.toUpperCase()}</b></div>
      ${b.test_name ? `<div class="row"><span>Test</span><b>${b.test_name}</b></div>` : ""}
      ${b.doctor_name ? `<div class="row"><span>Doctor</span><b>${b.doctor_name}</b></div>` : ""}
      <div class="row"><span>Date</span><b>${b.slot_date} ${b.slot_time}</b></div>
      <div class="row"><span>Patient</span><b>${b.patient.name} (${b.patient.age})</b></div>
      <div class="row"><span>Payment</span><b>${b.payment_status}</b></div>
      ${b.transaction_id ? `<div class="row"><span>Txn</span><b>${b.transaction_id}</b></div>` : ""}
      <div class="row total"><span>Total</span><b>₹${b.total_amount}</b></div>
      <p style="margin-top:24px;color:#64748B">Thank you for choosing AASAN OPD.</p>
      </body></html>`);
    w.document.close(); w.focus(); w.print();
  };

  if (loading || !user) return <div className="min-h-screen flex items-center justify-center">Loading…</div>;

  return (
    <div className={lang === "hi" ? "lang-hi" : ""}>
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
        <div className="flex items-center gap-4">
          {user.picture ? <img src={user.picture} alt="" className="w-14 h-14 rounded-full"/> :
            <div className="w-14 h-14 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-bold text-lg">{user.name[0]}</div>}
          <div>
            <h1 className="font-heading text-2xl font-bold">{t(lang, "common.welcome")}, {user.name}</h1>
            <p className="text-slate-500 text-sm">{user.email} · <span className="text-blue-600">{user.role}</span></p>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
          <StatCard color="blue" icon={CalendarDays} label={lang==="hi"?"बुकिंग्स":"Bookings"} value={bookings.length}/>
          <StatCard color="emerald" icon={Package} label={lang==="hi"?"ऑर्डर":"Orders"} value={orders.length}/>
          <StatCard color="amber" icon={IndianRupee} label={lang==="hi"?"खर्च":"Spent"} value={"₹" + (bookings.filter(b=>b.payment_status==="paid").reduce((s,b)=>s+b.total_amount,0) + orders.filter(o=>o.payment_status==="paid").reduce((s,o)=>s+o.total_amount,0))}/>
          <StatCard color="pink" icon={Bell} label="Notifs" value={notifs.filter(n=>!n.read).length}/>
        </div>

        <div className="mt-8 flex gap-2 flex-wrap">
          <TabBtn testid={DASH.tabBookings} active={tab==="bookings"} onClick={() => setTab("bookings")}>{lang==="hi"?"बुकिंग्स":"Bookings"}</TabBtn>
          <TabBtn testid={DASH.tabOrders} active={tab==="orders"} onClick={() => setTab("orders")}>{lang==="hi"?"फार्मेसी ऑर्डर":"Pharmacy Orders"}</TabBtn>
          <TabBtn active={tab==="notifs"} onClick={() => setTab("notifs")}>{lang==="hi"?"सूचनाएं":"Notifications"}</TabBtn>
        </div>

        {tab === "bookings" && (
          <div className="mt-6 space-y-3">
            {bookings.length === 0 && <p className="text-slate-500">{t(lang, "common.noBookings")}</p>}
            {bookings.map(b => (
              <div key={b.booking_id} data-testid={DASH.bookingRow(b.booking_id)} className="bg-white rounded-2xl card-shadow p-5 flex flex-wrap items-center gap-4">
                <div className="flex-1 min-w-[200px]">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="chip bg-blue-100 text-blue-700 uppercase">{b.service_type}</span>
                    <span className={`chip ${b.payment_status === "paid" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{b.payment_status}</span>
                    <span className="text-xs text-slate-500">Token: {b.token_number}</span>
                  </div>
                  <div className="mt-2 font-medium">{b.test_name || b.doctor_name || b.service_type}</div>
                  <div className="text-sm text-slate-500">{b.slot_date} · {b.slot_time} · {b.patient?.name}</div>
                </div>
                <div className="text-right">
                  <div className="font-heading text-lg font-semibold">₹{b.total_amount}</div>
                  <div className="flex items-center gap-3 mt-1 justify-end">
                    <button data-testid={DASH.downloadReceipt(b.booking_id)} onClick={() => printReceipt(b)} className="text-sm text-blue-600 inline-flex items-center gap-1">
                      <Download size={14}/> Receipt
                    </button>
                    {b.status !== "cancelled" && b.status !== "completed" && (
                      <button onClick={() => cancelBooking(b)} data-testid={`dash-cancel-${b.booking_id}`} className="text-sm text-red-500 inline-flex items-center gap-1">
                        <XCircle size={14}/> {lang==="hi"?"रद्द":"Cancel"}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        {tab === "orders" && (
          <div className="mt-6 space-y-3">
            {orders.length === 0 && <p className="text-slate-500">No orders yet</p>}
            {orders.map(o => (
              <div key={o.order_id} className="bg-white rounded-2xl card-shadow p-5">
                <div className="flex items-center gap-2">
                  <span className="chip bg-amber-100 text-amber-700">Pharmacy</span>
                  <span className={`chip ${o.payment_status === "paid" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{o.payment_status}</span>
                  <span className="text-xs text-slate-500">{o.order_id}</span>
                  <span className="ml-auto font-semibold">₹{o.total_amount}</span>
                </div>
                <div className="mt-2 text-sm text-slate-600">{o.items.map(i => `${i.name} × ${i.qty}`).join(", ")}</div>
                <div className="text-xs text-slate-500 mt-1">{o.delivery_address}</div>
              </div>
            ))}
          </div>
        )}
        {tab === "notifs" && (
          <div className="mt-6 space-y-3">
            {notifs.length === 0 && <p className="text-slate-500">No notifications</p>}
            {notifs.map(n => (
              <div key={n.notification_id} className="bg-white rounded-2xl card-shadow p-4 flex gap-3">
                <div className={`w-2 h-2 mt-2 rounded-full ${n.read ? "bg-slate-300" : "bg-blue-500"}`}/>
                <div>
                  <div className="font-medium">{n.title}</div>
                  <div className="text-sm text-slate-600">{n.message}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <WhatsAppFab />
      <Footer />
    </div>
  );
}

const StatCard = ({ color, icon: Icon, label, value }) => (
  <div className={`bg-white rounded-2xl card-shadow p-5`}>
    <div className={`w-10 h-10 rounded-xl bg-${color}-100 text-${color}-600 flex items-center justify-center`}><Icon size={20}/></div>
    <div className="text-2xl font-heading font-bold mt-3">{value}</div>
    <div className="text-sm text-slate-500">{label}</div>
  </div>
);

const TabBtn = ({ active, onClick, children, testid }) => (
  <button data-testid={testid} onClick={onClick}
    className={`px-4 py-2 rounded-full text-sm font-medium ${active ? "bg-blue-600 text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-blue-500"}`}>
    {children}
  </button>
);
